import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import api from "@/lib/api";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [annual, setAnnual] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => {}); }, []);

  const onSelectPlan = async (plan) => {
    if (plan.id === "enterprise") { window.location.href = "mailto:sales@dealsops.example?subject=DealsOps Enterprise"; return; }
    const lookup = annual ? plan.lookup_yearly : plan.lookup_monthly;
    if (user) { await startCheckout(lookup); } else { navigate(`/register?plan=${lookup}`); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicHeader />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="flex flex-col items-center text-center">
          <div className="eyebrow mb-4">Pricing</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl">A paid control plane. No free plan, no $49 tier.</h1>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="pricing-no-trial">No free plan. No trial. Demo is scheduled.</p>
          <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-border/70 p-1 bg-card/60">
            <button onClick={() => setAnnual(false)} data-testid="billing-monthly" className={`px-4 py-1.5 rounded-full text-sm transition-colors ${!annual ? "bg-amber-500 text-black" : "text-muted-foreground"}`}>Monthly</button>
            <button onClick={() => setAnnual(true)} data-testid="billing-annual" className={`px-4 py-1.5 rounded-full text-sm transition-colors ${annual ? "bg-amber-500 text-black" : "text-muted-foreground"}`}>Annual <span className="text-xs opacity-80">· 2 months free</span></button>
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-4 gap-5">
          {plans.map((p) => {
            const featured = p.id === "professional";
            const price = p.id === "enterprise" ? "Custom" : `$${annual ? Math.round(p.yearly / 12) : p.monthly}`;
            return (
              <div key={p.id} data-testid={`pricing-tier-${p.id}`} className={`rounded-xl border p-6 flex flex-col relative ${featured ? "border-amber-500/60 bg-card amber-glow" : "border-border/70 bg-card/50"}`}>
                {featured && <span className="absolute -top-2.5 left-6 text-[10px] font-mono uppercase tracking-wider bg-amber-500 text-black px-2 py-0.5 rounded-full">Most popular</span>}
                <div className="font-display text-lg font-semibold">{p.name}</div>
                <p className="text-xs text-muted-foreground mt-1 h-8">{p.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="metric text-4xl">{price}</span>
                  {p.id !== "enterprise" && <span className="text-sm text-muted-foreground">/mo</span>}
                </div>
                {p.id === "enterprise" && <span className="text-xs text-muted-foreground mt-1">from $2,000/mo</span>}
                {annual && p.id !== "enterprise" && <span className="text-xs text-emerald-500 mt-1">billed ${p.yearly}/yr</span>}
                <Button onClick={() => onSelectPlan(p)} data-testid={`select-${p.id}-btn`} className={`mt-5 rounded-md w-full ${featured ? "bg-amber-500 text-black hover:bg-amber-400" : ""}`} variant={featured ? "default" : "outline"}>
                  {p.id === "enterprise" ? "Contact sales" : "Choose plan"}
                </Button>
                <ul className="mt-6 space-y-2.5 text-sm">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5"><Check className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" /><span className="text-muted-foreground">{h}</span></li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-xs text-muted-foreground max-w-2xl mx-auto">
          Add-ons: Apollo credit packs ($99 / $249 / $499), extra send volume ($79 / $149). Bring your own Navigator license — DealsOps never scrapes LinkedIn.
        </p>
      </section>
      <PublicFooter />
    </div>
  );
}
